import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

/**
 * Async thunk to fetch cascade filter settings from backend
 */
export const fetchCascadeSettings = createAsyncThunk('cascadeFilter/fetchSettings', async () => {
  try {
    const token = Cookies.get('token');

    // Try the user notification preferences endpoint which contains the actual data
    const response = await fetch(
      process.env.REACT_APP_BASE_URL + 'notifications/user-notification-preferences',
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      // If no saved settings found, return null to use default state
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle empty response
    const responseText = await response.text();
    if (!responseText) {
      return null;
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (jsonError) {
      return null;
    }

    // Check if we have valid data
    if (
      !data ||
      (!data.notify &&
        !data.locations &&
        !data.machines &&
        !data.machineGroups &&
        !data.maintenanceTypes)
    ) {
      return null;
    }

    // Also check if all arrays are empty (database cleared case)
    const hasNoData =
      (!data.locations || data.locations.length === 0) &&
      (!data.machines || data.machines.length === 0) &&
      (!data.machineGroups || data.machineGroups.length === 0) &&
      (!data.maintenanceTypes || data.maintenanceTypes.length === 0);

    if (hasNoData) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching cascade settings:', error);
    throw error;
  }
});

/**
 * Async thunk to save cascade filter settings to backend
 */
export const saveCascadeSettings = createAsyncThunk(
  'cascadeFilter/saveSettings',
  async (settingsData, { getState }) => {
    try {
      const token = Cookies.get('token');
      const currentState = getState().cascadeFilter;
      const notificationsState = getState().notifications;

      // Include cascade context in the regular notifications save
      const dataToSave = {
        // Current notification settings
        machines: notificationsState.selectedFilters?.machines || [],
        machineGroups: notificationsState.selectedFilters?.machineGroups || [],
        locations: notificationsState.selectedFilters?.locations || [],
        maintenanceTypes: notificationsState.selectedFilters?.maintenanceTypes || [],
        enabled: notificationsState.enabled ?? true,
        // Add cascade context
        cascadeContext: {
          primaryFilter: settingsData.primaryFilter || currentState.primaryFilter,
          primarySelection: settingsData.primarySelection || currentState.primarySelection,
          showAllFilters:
            settingsData.showAllFilters !== undefined
              ? settingsData.showAllFilters
              : currentState.showAllFilters,
          lastSaved: new Date().toISOString(),
        },
      };

      const response = await fetch(process.env.REACT_APP_BASE_URL + 'notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.warn('Cascade settings endpoint not implemented yet (404) - skipping save');
          // Return mock success data for development
          return {
            ...dataToSave,
            message: 'Endpoint not implemented - data not saved',
          };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle empty response (like in saveNotifications)
      const responseText = await response.text();
      if (!responseText) {
        return { success: true, status: response.status, ...dataToSave };
      }

      try {
        return JSON.parse(responseText);
      } catch (error) {
        console.warn('saveCascadeSettings - response is not JSON:', responseText);
        return { success: true, status: response.status, message: responseText, ...dataToSave };
      }
    } catch (error) {
      console.error('Error saving cascade settings:', error);
      throw error;
    }
  }
);

/**
 * Async thunk to clear saved cascade filter settings
 */
export const clearCascadeSettings = createAsyncThunk('cascadeFilter/clearSettings', async () => {
  try {
    const token = Cookies.get('token');

    const response = await fetch(process.env.REACT_APP_BASE_URL + 'cascade-filter-settings', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('Cascade settings endpoint not implemented yet (404) - skipping delete');
        return true;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('Error clearing cascade settings:', error);
    throw error;
  }
});

/**
 * Initial state for cascade filter settings
 */
const initialState = {
  // Saved cascade filter state
  primaryFilter: '',
  primarySelection: [],
  showAllFilters: false,
  selectedFilters: {
    locations: [],
    machineGroups: [],
    machines: [],
    maintenanceTypes: [],
  },

  // API response data (for detailed display)
  notify: null,
  locations: null,
  machineGroups: null,
  machines: null,
  maintenanceTypes: null,

  // Metadata
  lastSaved: null,
  hasSavedSettings: false,

  // Loading states
  isLoading: false,
  isSaving: false,
  isClearing: false,

  // Error handling
  error: null,
  saveError: null,
};

/**
 * Sync cascade filter with notification data
 */
export const syncWithNotificationData = (notificationData) => (dispatch, getState) => {
  if (notificationData && (notificationData.notify || notificationData.locations)) {
    // Update cascade state with notification data
    dispatch(
      updateCascadeStateFromNotifications({
        notify: notificationData.notify,
        locations: notificationData.locations,
        machineGroups: notificationData.machineGroups,
        machines: notificationData.machines,
        maintenanceTypes: notificationData.maintenanceTypes,
      })
    );
  }
};

/**
 * Cascade filter slice
 */
const cascadeFilterSlice = createSlice({
  name: 'cascadeFilter',
  initialState,
  reducers: {
    // Update cascade state from notification data
    updateCascadeStateFromNotifications: (state, action) => {
      const { notify, locations, machineGroups, machines, maintenanceTypes } = action.payload;

      // Check if we actually have saved data (not just empty arrays)
      const hasActualData =
        notify &&
        ((Array.isArray(locations) && locations.length > 0) ||
          (Array.isArray(machineGroups) && machineGroups.length > 0) ||
          (Array.isArray(machines) && machines.length > 0) ||
          (Array.isArray(maintenanceTypes) && maintenanceTypes.length > 0));

      if (hasActualData) {
        // Store the full response for display
        state.notify = notify;
        state.locations = locations || [];
        state.machineGroups = machineGroups || [];
        state.machines = machines || [];
        state.maintenanceTypes = maintenanceTypes || [];

        // Extract IDs for the selectedFilters format
        state.selectedFilters = {
          locations: locations?.map((item) => Number(item.location?.id)) || [],
          machineGroups: machineGroups?.map((item) => Number(item.machine_group?.id)) || [],
          machines: machines?.map((item) => Number(item.machine?.id)) || [],
          maintenanceTypes:
            maintenanceTypes?.map((item) => Number(item.maintenance_type?.id)) || [],
        };

        // Default cascade context - no filters active, show saved settings
        state.primaryFilter = '';
        state.primarySelection = [];
        state.showAllFilters = false;
        state.lastSaved = notify?.updated_at || new Date().toISOString();
        state.hasSavedSettings = true;
      } else {
        // No saved settings found - clear everything
        state.hasSavedSettings = false;
        state.lastSaved = null;
        state.notify = null;
        state.locations = null;
        state.machineGroups = null;
        state.machines = null;
        state.maintenanceTypes = null;
      }
    },
    // Update current cascade state (without saving to backend)
    updateCascadeState: (state, action) => {
      const { primaryFilter, primarySelection, showAllFilters, selectedFilters } = action.payload;

      if (primaryFilter !== undefined) state.primaryFilter = primaryFilter;
      if (primarySelection !== undefined) state.primarySelection = primarySelection;
      if (showAllFilters !== undefined) state.showAllFilters = showAllFilters;
      if (selectedFilters !== undefined)
        state.selectedFilters = { ...state.selectedFilters, ...selectedFilters };
    },

    // Clear current state (reset to initial)
    resetCascadeState: (state) => {
      state.primaryFilter = '';
      state.primarySelection = [];
      state.showAllFilters = false;
      state.selectedFilters = {
        locations: [],
        machineGroups: [],
        machines: [],
        maintenanceTypes: [],
      };
      // Clear saved settings flag and data
      state.hasSavedSettings = false;
      state.lastSaved = null;
      state.notify = null;
      state.locations = null;
      state.machineGroups = null;
      state.machines = null;
      state.maintenanceTypes = null;
    },

    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch cascade settings
    builder.addCase(fetchCascadeSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchCascadeSettings.fulfilled, (state, action) => {
      state.isLoading = false;

      if (action.payload) {
        // Check if we have the new API format with detailed data
        if (action.payload.notify && action.payload.locations) {
          // Store the full response for display in ResultsPanel
          state.notify = action.payload.notify;
          state.locations = action.payload.locations;
          state.machineGroups = action.payload.machineGroups;
          state.machines = action.payload.machines;
          state.maintenanceTypes = action.payload.maintenanceTypes;

          // Extract IDs for the selectedFilters format
          state.selectedFilters = {
            locations: action.payload.locations?.map((item) => Number(item.location?.id)) || [],
            machineGroups:
              action.payload.machineGroups?.map((item) => Number(item.machine_group?.id)) || [],
            machines: action.payload.machines?.map((item) => Number(item.machine?.id)) || [],
            maintenanceTypes:
              action.payload.maintenanceTypes?.map((item) => Number(item.maintenance_type?.id)) ||
              [],
          };

          // Load cascade context if available
          if (action.payload.cascadeContext) {
            state.primaryFilter = action.payload.cascadeContext.primaryFilter || '';
            state.primarySelection = action.payload.cascadeContext.primarySelection || [];
            state.showAllFilters = action.payload.cascadeContext.showAllFilters || false;
            state.lastSaved =
              action.payload.cascadeContext.lastSaved || action.payload.notify?.updated_at;
          } else {
            // Default cascade context - no filters active, show saved settings
            state.primaryFilter = '';
            state.primarySelection = [];
            state.showAllFilters = false;
            state.lastSaved = action.payload.notify?.updated_at || new Date().toISOString();
          }

          state.hasSavedSettings = true;
        } else if (action.payload.selectedFilters) {
          // Fallback to old format
          state.primaryFilter = action.payload.primaryFilter || '';
          state.primarySelection = action.payload.primarySelection || [];
          state.showAllFilters = action.payload.showAllFilters || false;
          state.selectedFilters = action.payload.selectedFilters || initialState.selectedFilters;
          state.lastSaved = action.payload.lastSaved;
          state.hasSavedSettings = true;
        } else {
          // No recognized format
          state.hasSavedSettings = false;
        }
      } else {
        // No saved settings found
        state.hasSavedSettings = false;
      }

      state.error = null;
    });
    builder.addCase(fetchCascadeSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
      state.hasSavedSettings = false;
    });

    // Save cascade settings
    builder.addCase(saveCascadeSettings.pending, (state) => {
      state.isSaving = true;
      state.saveError = null;
    });
    builder.addCase(saveCascadeSettings.fulfilled, (state, action) => {
      state.isSaving = false;
      state.lastSaved = action.payload.lastSaved;
      state.hasSavedSettings = true;
      state.saveError = null;
    });
    builder.addCase(saveCascadeSettings.rejected, (state, action) => {
      state.isSaving = false;
      state.saveError = action.error.message;
    });

    // Clear cascade settings
    builder.addCase(clearCascadeSettings.pending, (state) => {
      state.isClearing = true;
      state.error = null;
    });
    builder.addCase(clearCascadeSettings.fulfilled, (state) => {
      state.isClearing = false;
      state.hasSavedSettings = false;
      state.lastSaved = null;
      state.error = null;
      // Reset to initial state
      state.primaryFilter = '';
      state.primarySelection = [];
      state.showAllFilters = false;
      state.selectedFilters = initialState.selectedFilters;
    });
    builder.addCase(clearCascadeSettings.rejected, (state, action) => {
      state.isClearing = false;
      state.error = action.error.message;
    });
  },
});

// Export actions
export const {
  updateCascadeState,
  resetCascadeState,
  clearErrors,
  updateCascadeStateFromNotifications,
} = cascadeFilterSlice.actions;

// Export reducer
export default cascadeFilterSlice.reducer;
